import WireObjectProxy from './WireObjectProxy';
import { GLOBAL_ID } from '../constants';
import { isNil, isNotNil } from '../util/object';
import { getCanvasRelativeElementTransformAtFrame } from '../util/transform';
import Layer from './Layer';

/**
 * A wrapper for a Template wire object.
 */
export default class Template extends WireObjectProxy {
  /** Template specific data from the JSON string in the template's wireObject. */
  _templateData = null;

  constructor(wireObject) {
    super(wireObject);
    // @todo(chuti): refactor wireObjectProxy
    this._templateData = isNotNil(this._wireObject.data)
      ? JSON.parse(this._wireObject.data)
      : {};
  }

  copy() {
    this._wireObject.data = JSON.stringify(this._templateData);
    return super.copy();
  }

  get active() {
    return this._wireObject.active;
  }

  set active(active) {
    this._wireObject.active = active;
  }

  get adAccountId() {
    return this._wireObject.ad_account_id;
  }

  set adAccountId(adAccountId) {
    this._wireObject.ad_account_id = adAccountId;
  }

  get category() {
    return this._wireObject.category;
  }

  set category(category) {
    this._wireObject.category = category;
  }

  get content() {
    return this._templateData.content;
  }

  set content(content) {
    this._templateData.content = content;
  }

  get createdAt() {
    return this._wireObject.created_at;
  }

  set createdAt(createdAt) {
    this._wireObject.created_at = createdAt;
  }

  get data() {
    return this._wireObject.data;
  }

  set data(data) {
    this._wireObject.data = data;
  }

  get description() {
    return this._wireObject.description;
  }

  set description(description) {
    this._wireObject.description = description;
  }

  get elements() {
    return this._templateData.elements;
  }

  set elements(elements) {
    this._templateData.elements = elements;
  }

  get id() {
    return this._wireObject.id;
  }

  set id(id) {
    this._wireObject.id = id;
  }

  get imagePlaceholderUrl() {
    return this._templateData.imagePlaceholderUrl;
  }

  set imagePlaceholderUrl(imagePlaceholderUrl) {
    this._templateData.imagePlaceholderUrl = imagePlaceholderUrl;
  }

  get media() {
    return this._templateData.media;
  }

  set media(media) {
    this._templateData.media = media;
  }

  get name() {
    return this._wireObject.name;
  }

  set name(name) {
    this._wireObject.name = name;
  }

  get ordinal() {
    return this._wireObject.ordinal;
  }

  set ordinal(ordinal) {
    this._wireObject.ordinal = ordinal;
  }

  get organizationId() {
    return this._wireObject.organization_id;
  }

  set organizationId(organizationId) {
    this._wireObject.organization_id = organizationId;
  }

  get publisherId() {
    return this._wireObject.publisher_id;
  }

  set publisherId(publisherId) {
    this._wireObject.publisher_id = publisherId;
  }

  get rootFrameEnd() {
    return this._templateData.elements.masks.root.frameEnd;
  }

  get tags() {
    return this._wireObject.tags;
  }

  set tags(tags) {
    this._wireObject.tags = tags;
  }

  get templateAspectRatio() {
    return this.templateWidth / this.templateHeight;
  }

  get templateData() {
    return this._templateData;
  }

  get templateHeight() {
    return this._templateData.elements.masks.root.height;
  }

  get templateWidth() {
    return this._templateData.elements.masks.root.width;
  }

  get text() {
    return this._templateData.text;
  }

  set text(text) {
    this._templateData.text = text;
  }

  get updatedAt() {
    return this._wireObject.updated_at;
  }

  set updatedAt(updatedAt) {
    this._wireObject.updated_at = updatedAt;
  }

  get version() {
    return this._templateData.version;
  }

  set version(version) {
    this._templateData.version = version;
  }

  get videoUrl() {
    return this._templateData.videoUrl;
  }

  set videoUrl(videoUrl) {
    this._templateData.videoUrl = videoUrl;
  }

  getContentByElementId(elementId) {
    const element = this.getElementByElementId(elementId);
    if (isNil(element)) return null;

    const { contentId } = element;
    return isNotNil(this._templateData.content) &&
      isNotNil(this._templateData.content.contentById)
      ? this._templateData.content.contentById[contentId]
      : null;
  }

  getElementByElementId(elementId) {
    if (
      isNil(this._templateData.elements) ||
      isNil(this._templateData.elements.elementsById) ||
      isNil(elementId)
    ) {
      return null;
    }
    return this._templateData.elements.elementsById[elementId];
  }

  getElementByName(name) {
    if (
      isNil(this._templateData.elements) ||
      isNil(this._templateData.elements.elementsById) ||
      isNil(name)
    ) {
      return null;
    }
    return Object.values(this._templateData.elements.elementsById).find(
      (element) => {
        return element.name === name;
      },
    );
  }

  getMediaByElementId(elementId) {
    const content = this.getContentByElementId(elementId);
    if (isNil(content)) return null;

    const { mediaId } = content;
    return isNotNil(this._templateData.media) &&
      isNotNil(this._templateData.media.mediaById)
      ? this._templateData.media.mediaById[mediaId]
      : null;
  }

  getCanvasRelativeElementTransformAtFrame(elementId, frameNumber) {
    const elementGetter = (elementId) => this.getElementByElementId(elementId);
    return getCanvasRelativeElementTransformAtFrame(
      elementId,
      frameNumber,
      false,
      elementGetter,
    );
  }

  getLayer(elementId) {
    const content = this.getContentByElementId(elementId);
    const element = this.getElementByElementId(elementId);
    const media = this.getMediaByElementId(elementId);

    if (isNil(element) || isNil(content) || isNil(media)) return undefined;

    return Layer.getLayerFromReduxStateObjects(element, content, media);
  }

  getLayerByName(name) {
    const element = this.getElementByName(name);
    if (isNil(element)) return undefined;

    const content = this.getContentByElementId(element.elementId);
    const media = this.getMediaByElementId(element.elementId);

    if (isNil(content) || isNil(media)) return undefined;

    return Layer.getLayerFromReduxStateObjects(element, content, media);
  }

  getLayerBounds(elementId) {
    const layer = this.getLayer(elementId);
    if (!Layer.isLayer(layer)) return undefined;

    return layer.bounds;
  }

  isGlobal() {
    return (
      this.publisherId === GLOBAL_ID ||
      this.organizationId === GLOBAL_ID
    );
  }

  /**
   * Checks if object is an instance of Template
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isTemplate(inputObject) {
    return inputObject instanceof Template;
  }
}
